/**
 * How the browser reacts to "the database is waking up".
 *
 * ⚠ THE SHAPE OF THE PROBLEM. Azure SQL runs serverless and pauses after an
 * idle hour; waking it takes 30–60 seconds. Static Web Apps cuts every /api
 * request off at 45 seconds, and that limit cannot be configured. So the first
 * person to open AcuTidy after a quiet spell cannot be served by waiting — the
 * gateway will answer before the database does.
 *
 * The API therefore gives up at 25 seconds and returns 503 with this code,
 * rather than working on past the point anyone can receive the answer. The
 * browser is what turns that into patience: it waits and asks again, which is
 * exactly what a person would do, and it knows to do it without being told.
 *
 * Extracted so the decision can be tested without a server, a database, or a
 * minute of waiting.
 */
export const WAKING_CODE = 'database_waking';

/** Attempts AFTER the first, and how long to wait before each. */
export const WAKE_RETRY_DELAYS_MS = [6_000, 12_000, 18_000];

export function isWakingResponse(status: number, body: unknown): boolean {
  if (status !== 503) return false;
  return (body as { code?: string } | undefined)?.code === WAKING_CODE;
}

/**
 * How long to wait before attempt number `attempt` (1-based), or null when
 * there are no attempts left.
 *
 * ⚠ Bounded on purpose. A database that never wakes is a fault, and retrying
 * forever would present a broken service as a slow one — the customer would sit
 * watching a spinner instead of being told something is wrong.
 */
export function wakeRetryDelay(attempt: number): number | null {
  return WAKE_RETRY_DELAYS_MS[attempt - 1] ?? null;
}

/**
 * Methods the browser may replay by itself when the database was merely waking.
 *
 * ⚠ READS ONLY, AND THE COMMENT IN api.ts USED TO CLAIM THIS WITHOUT THE CODE
 * DOING IT. Every method was retried, including POST.
 *
 * It has not bitten yet because a waking database fails before the handler
 * reaches its side effect — the 503 comes from getPool, not from the work. But
 * that is a property of today's call order, not a guarantee. The moment any
 * handler returns `database_waking` AFTER something has happened, a blind
 * replay submits it twice: two write jobs on a customer's ERP, two Checkout
 * sessions, two disconnects, two merges.
 *
 * ⚠ A WRITE THAT MEETS A WAKING DATABASE IS NOT LOST — it is handed back to
 * the person. They see the 503's own copy, which explains that the database
 * pauses when idle, and they press the button again. That retry is deliberate
 * rather than automatic, which is exactly the property a non-idempotent
 * request needs, and by then the pool is usually warm because the shared
 * connect attempt kept going without them.
 *
 * The alternative considered was an Idempotency-Key on mutating routes. That is
 * the right answer if automatic write retries are ever wanted, but it is real
 * server work — a key store, a replay window, a decision about what "the same
 * request" means — to buy back something a human already does well.
 */
const REPLAYABLE_METHODS = new Set(['GET', 'HEAD']);

export function shouldRetryWake(method: string | undefined): boolean {
  // fetch defaults to GET when no method is given.
  return REPLAYABLE_METHODS.has((method ?? 'GET').toUpperCase());
}
